import { APP_DASHBOARD_SECTION_PERMISSIONS, BASELINE_PERMISSIONS } from '_constants/PERMISSIONS';

export const getAuthenticatedUser = (state) => state.auth.isAuthenticated;
export const getIsValidLink = (state) => state.auth.isValidLinkMessage;
export const getUserEmail = (state) => state.auth.email;
export const getUserFirstName = (state) => state.auth.firstName;
export const getUserLastName = (state) => state.auth.lastName;
export const getUserState = (state) => state.auth.stateAddress;
export const getAppCanStart = (state) => state.auth.appCanStart;
export const getIsSuperuser = (state) => state.auth.isSuperuser;
export const getProfilePermissions = (state) => state.auth.permissions;

// P20M-1309 — App Dashboard section permissions. Superusers always pass (they
// hold every permission on the backend); a non-superuser needs at least one of
// the section permissions to reach the App Dashboard at all. The list lives in
// _constants (shared with the route guard); re-exported here for existing imports.
export { APP_DASHBOARD_SECTION_PERMISSIONS };
export const getCanAccessAppDashboard = (state) => {
  if (state.auth.isSuperuser) return true;
  const permissions = state.auth.permissions || [];
  return APP_DASHBOARD_SECTION_PERMISSIONS.some((permission) => permissions.includes(permission));
};

// P20M-1327 — a user who can open the App Dashboard but holds NO tenant
// permission (e.g. a zero-permission "Visitor" role plus view_govramp for a
// DevOps evidence collector). The tenant Dashboard has nothing to show them, so
// the app sends them straight to the App Dashboard and the menu offers only that.
//
// "Tenant permissions" = the role's own permissions (detailedPermissions, loaded
// with the profile) minus the baseline grants default roles carry (view_profile /
// view_account). Since P20M-1327 the backend serves /api/profile reads without
// view_profile (the endpoint is self-scoped), so a role may hold view_govramp
// alone — but roles that still carry the baseline stay dashboard-only too, hence
// the subtraction is kept. Before the profile arrives (right
// after sign-in) that list is empty, so fall back to `permissions` minus the
// modularity list — account modules then still count as tenant permissions and
// the check stays false until the real data is in, which errs on the safe side
// (no premature redirect).
export const getIsAppDashboardOnlyUser = (state) => {
  if (state.auth.isSuperuser || !getCanAccessAppDashboard(state)) return false;
  const permissions = state.auth.permissions || [];
  const modularityPermissions = state.auth.modularityPermissions || [];
  const detailedPermissions = state.auth.detailedPermissions || [];
  const rolePermissions = detailedPermissions.length
    ? detailedPermissions.map(({ codename }) => codename)
    : permissions.filter((permission) => !modularityPermissions.includes(permission));
  return rolePermissions.every(
    (permission) => APP_DASHBOARD_SECTION_PERMISSIONS.includes(permission) || BASELINE_PERMISSIONS.includes(permission),
  );
};
export const getAgencies = (state) => state.auth.agencies;
export const getUserId = (state) => state.auth.userId;
export const getAllUserInfo = (state) => state.auth;
export const getUserGuideLink = (state) => state.auth.userGuideLink;
export const getAgenciesList = (state) => state.auth.agenciesList;
export const getSecGeogList = (state) => state.auth.secGeogList;
export const getRoleName = (state) => state.auth.role.attributes?.name;
// The account the role belongs to: each account has its own roles (its own "Multi-Account Manager").
export const getRoleAccountName = (state) => state.auth.role?.attributes?.account_name;
export const getCreatedCampaignByMe = (state) => state.auth.campaignIds;
export const getUserRoleType = (state) => state.auth?.role?.attributes?.role_type;
export const getUserSubdomain = (state) => state.auth.subdomain;
export const getPermissionStatus = (state) => state.auth.permissionStatus;
export const getModularityPermissions = (state) => state.auth.modularityPermissions;
export const getAccountModules = (state) => state.auth.accountModules;
export const getDashboardLinks = (state) => state.auth.dashboardLinks;
export const getDashboardLinksAccount = (state) => state.auth.dashboardLinksAccount;
export const getTimeZone = (state) => state.auth.timeZone;
export const getRetailersImportInfo = (state) => state.auth.retailersImport;
export const getUsersImportInfo = (state) => state.auth.usersImport;
export const getAccountProductInfo = (state) => state.auth.accountProduct;
export const getDefaultDirection = (state) => state.auth.defaultDirection;
export const getUserStateName = (state) => state.auth.stateName;
export const getDetailedPermissions = (state) => state.auth.detailedPermissions;
